import { useEffect, useRef, useState } from 'react';
import { useStorefront } from './useStorefront';
import WarpPipeCart from './WarpPipeCart';

/**
 * CartButton — warp-pipe icon + floating count badge.
 *
 * The badge hovers gently above the pipe at all times. Every time an item
 * is added it plays a "sucked into the pipe" animation: speed lines streak
 * upward while the badge dives down into the lip of the pipe, then springs
 * back up showing the new count.
 */
const CartButton = ({ onOpen }) => {
  const { cartCount, onItemAdded } = useStorefront();
  const [pulse, setPulse] = useState(0); // increments to retrigger CSS animation
  const timerRef = useRef(null);

  useEffect(() => {
    const unsub = onItemAdded(() => {
      setPulse((p) => p + 1);
      if (timerRef.current) clearTimeout(timerRef.current);
      // Auto-clear class so the next add can retrigger cleanly
      timerRef.current = setTimeout(() => setPulse((p) => p), 900);
    });
    return () => { unsub(); if (timerRef.current) clearTimeout(timerRef.current); };
  }, [onItemAdded]);

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Open cart, ${cartCount} ${cartCount === 1 ? 'item' : 'items'}`}
      className="sf-cart-button"
      data-testid="sf-nav-cart"
      title="Open cart"
    >
      <WarpPipeCart size={32} />

      {/* Speed lines that streak upward when an item is sucked in */}
      <span
        key={`lines-${pulse}`}
        className={`sf-cart-speedlines ${pulse > 0 ? 'play' : ''}`}
        aria-hidden="true"
      >
        <i /><i /><i /><i /><i />
      </span>

      {/* Floating count badge */}
      {cartCount > 0 && (
        <span
          key={`badge-${pulse}-${cartCount}`}
          className={`sf-cart-badge ${pulse > 0 ? 'suck' : ''}`}
          data-testid="sf-cart-badge"
        >
          {cartCount > 99 ? '99+' : cartCount}
        </span>
      )}
    </button>
  );
};

export default CartButton;
